import { useRef, useMemo } from 'react';
import { generatePath, useLocation, NavLink, Outlet } from 'react-router-dom';
import {
  IconLoader,
  Table,
  IconSelected,
  IconArrowRight,
  Popup,
  InputText,
  IconSearch,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { setHintVisible, selectList } from '../store/slices/list';
import { CustomerProps } from '../../common/types';
import {
  customersListColumns,
  customersListColumnsLabels,
} from './variables/list';
import formatIndividualCustomersList from './helpers/format-individual-customers-list';
import useIndividualList from './hooks/use-list';
import TopContent from './parts/top-content';

import ROUTES from '../../../routes';

import commonStyles from '../../common/common.module.css';

const IndividualList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { dictionariesIsLoading, applicationSchemes } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const {
    individualCustomers,
    hintVisible,
    createdApplicationId,
    pagination,
    selectedLists,
    errorsList,
  } = useAppSelector((state) => state.individualCustomers.list);
  const { baseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const { generalUserInfo, baseUserInformation } = useAppSelector(
    (state) => state.individualCustomers.info.overview,
  );
  const { countriesList: countries, activePermissions } = useAppSelector(
    (state) => state.root,
  );

  const { search } = useLocation();

  const {
    nameSearch,
    updateSelectedListVisible,
    setUpdateSelectedListVisible,
    updateAllListVisible,
    setUpdateAllListVisible,
    action,
    setAction,
    popupVisible,
    setPopupVisible,
    isLoading,
    errorList,
    filtersComponent,
    handleSubmit,
    countriesList,
    filters,
    isFetching,
    onCustomerSearch,
    onTableFilterChange,
    onTableSetPage,
  } = useIndividualList({
    pagination,
    countries,
    scrollRef,
    baseInformation,
    generalUserInfo,
    baseUserInformation,
    applicationSchemes,
    activePermissions,
    individualCustomers,
    search,
  });

  const dispatch = useAppDispatch();

  const formattedData = useMemo(() => {
    const data = formatIndividualCustomersList(
      individualCustomers as CustomerProps[],
      countries,
    );

    return data;
  }, [individualCustomers]);

  let content;
  let popupTitle;
  let popupButtonText = 'Yes, Apply';

  if (action === 'updateSelected') {
    popupTitle = `Calculated risk score will be updated for ${
      selectedLists.length
    } client${selectList?.length > 1 ? 's' : ''}`;

    popupButtonText = 'Yes, update';
  } else if (action === 'applySelected') {
    popupTitle = `Current calculated risk score will be applied for ${
      selectedLists.length
    } client${selectList?.length > 1 ? 's' : ''}`;
  } else if (action === 'updateAndApplySelected') {
    popupTitle = `Calculated Risk Score will be updated and applied for ${
      selectedLists.length
    } client${selectList?.length > 1 ? 's' : ''}`;
  } else if (action === 'updateAll') {
    popupTitle = 'Calculated risk score will be updated for all clients';
    popupButtonText = 'Yes, update';
  } else if (action === 'applyAll') {
    popupTitle =
      'Current calculated risk score will be applied for all clients';
  } else if (action === 'updateAndApplyAll') {
    popupTitle =
      'Calculated Risk Score will be updated and applied for all clients';
  }

  if (isLoading || dictionariesIsLoading) {
    content = (
      <div className={commonStyles.customersLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && countriesList) {
    content = (
      <Table
        columns={customersListColumns}
        data={formattedData || []}
        sort={{}}
        onSort={() => {}}
        filters={filters}
        error={errorsList || errorList}
        columnsLabels={customersListColumnsLabels}
        tableFormClassName={commonStyles.customersListForm}
        onFilterChange={(values) => onTableFilterChange(values)}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        hiddenFiltersTags={
          filters?.status?.value.length === 2 ? ['status'] : []
        }
        take="20"
        setPage={(data) => onTableSetPage(data)}
        pagination={pagination}
        withSettings
        clickable
      />
    );
  }

  return (
    <div className={commonStyles.customersBottom} ref={scrollRef}>
      <div className={commonStyles.customersTopContent}>
        <div className={commonStyles.customerTopContentTitle}>
          Individual Customers
        </div>
        <TopContent
          search={search}
          selectedLists={selectedLists}
          activePermissions={activePermissions}
          updateSelectedListVisible={updateSelectedListVisible}
          setUpdateSelectedListVisible={setUpdateSelectedListVisible}
          setPopupVisible={setPopupVisible}
          setAction={setAction}
          updateAllListVisible={updateAllListVisible}
          setUpdateAllListVisible={setUpdateAllListVisible}
        />
        <InputText
          name="nameSearch"
          labelClassName={commonStyles.nameSearch}
          value={nameSearch}
          placeholder="Enter name"
          prefix={<IconSearch />}
          prefixClassName={commonStyles.nameSearchIcon}
          onChange={(e) => onCustomerSearch(e.target.value)}
        />
      </div>
      {content}
      <Outlet />
      {hintVisible && (
        <div className={commonStyles.applicationCreatedSuccessful}>
          <div className={commonStyles.applicationCreatedSuccessfulLeft}>
            <span>
              <IconSelected />
            </span>
            <span>Customer created</span>
          </div>
          <div className={commonStyles.applicationCreatedSuccessfulRight}>
            <span>
              <NavLink
                to={generatePath(
                  ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO,
                  { applicationId: createdApplicationId as string },
                )}
              >
                <IconArrowRight />
                Open application
              </NavLink>
            </span>
            <button
              type="button"
              className={commonStyles.hintCloseButton}
              onClick={() => dispatch(setHintVisible(false))}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <Popup
        title={popupTitle}
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => setPopupVisible(false)}
        cancelButtonText="Cancel"
        okButtonText={popupButtonText}
        isVisible={popupVisible}
      />
    </div>
  );
};

export default IndividualList;
